import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ShieldCheck, 
  Package, 
  RefreshCw, 
  CreditCard, 
  AlertCircle,
  CheckCircle,
  Mail,
  Clock,
  ArrowLeft
} from 'lucide-react';

const ReturnPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="return-policy-page">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <ShieldCheck className="h-8 w-8 text-primary" />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
          Return & Refund Policy
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Your satisfaction is our priority. We stand behind the quality of our products 
          and want you to be completely happy with your purchase.
        </p>
      </div>

      {/* Main Policy Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {/* Option 1: Full Refund */}
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-serif text-xl font-bold text-foreground">Option 1</h2>
                <p className="text-sm text-primary font-medium">Full Refund</p>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Return the damaged or defective item and receive a <strong className="text-foreground">100% refund</strong> of your purchase price.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Full purchase price refunded</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Return shipping label provided</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Refund processed within 5-7 business days</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Option 2: Keep & Get Credit */}
        <Card className="border-2 border-amber-200 bg-amber-50/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="font-serif text-xl font-bold text-foreground">Option 2</h2>
                <p className="text-sm text-amber-600 font-medium">Keep It + 50% Credit</p>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Keep the item and receive <strong className="text-foreground">50% store credit</strong> to use on your next purchase.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Keep the item - no return needed</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>50% credit applied to your account</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Credit never expires</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Eligibility Section */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="font-serif text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            What&apos;s Covered
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Eligible for Return/Credit
              </h3>
              <ul className="space-y-1 text-sm text-muted-foreground ml-6">
                <li>• Damaged items (broken, cracked, chipped)</li>
                <li>• Defective products that don&apos;t work as described</li>
                <li>• Items damaged during shipping</li>
                <li>• Wrong item received</li>
                <li>• Missing parts or components</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                Not Eligible
              </h3>
              <ul className="space-y-1 text-sm text-muted-foreground ml-6">
                <li>• Change of mind purchases</li>
                <li>• E-books and digital downloads</li>
                <li>• Items damaged by customer misuse</li>
                <li>• Items without proof of purchase</li>
                <li>• Requests made after 30 days</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Request */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="font-serif text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            How to Request a Return or Credit
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="font-medium text-foreground">Contact Us</h3>
                <p className="text-sm text-muted-foreground">
                  Send us a message through our <Link to="/contact" className="text-primary hover:underline">Contact page</Link> or 
                  email us with your order number and a description of the issue.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="font-medium text-foreground">Provide Photos</h3>
                <p className="text-sm text-muted-foreground">
                  Include clear photos of the damaged or defective item to help us process your request quickly.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="font-medium text-foreground">Choose Your Option</h3>
                <p className="text-sm text-muted-foreground">
                  Let us know if you&apos;d prefer a full refund (Option 1) or to keep the item with 50% credit (Option 2).
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="font-medium text-foreground">We&apos;ll Take Care of the Rest</h3>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll review your request within 24-48 hours and process your refund or credit promptly.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="mb-8 bg-muted/30">
        <CardContent className="p-6">
          <h2 className="font-serif text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Important Timeframes
          </h2>
          <div className="grid sm:grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-background rounded-lg">
              <p className="text-2xl font-bold text-primary">30</p>
              <p className="text-sm text-muted-foreground">Days to report issues</p>
            </div>
            <div className="p-4 bg-background rounded-lg">
              <p className="text-2xl font-bold text-primary">24-48</p>
              <p className="text-sm text-muted-foreground">Hours to review requests</p>
            </div>
            <div className="p-4 bg-background rounded-lg">
              <p className="text-2xl font-bold text-primary">5-7</p>
              <p className="text-sm text-muted-foreground">Days for refund processing</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact CTA */}
      <div className="text-center bg-primary/5 rounded-xl p-8">
        <h2 className="font-serif text-xl font-bold text-foreground mb-2">
          Have Questions?
        </h2>
        <p className="text-muted-foreground mb-4">
          We&apos;re here to help! Reach out and we&apos;ll get back to you as soon as possible.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/contact">
            <Button className="btn-accent">
              <Mail className="h-4 w-4 mr-2" />
              Contact Us
            </Button>
          </Link>
          <Link to="/products">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicy;
